import React from 'react';
import { Box, Typography, Paper, Tooltip } from '@mui/material';
import {
  ResponsiveContainer,
  ScatterChart,
  ReferenceArea,
} from 'recharts';

const FlowStateLegend = () => {
  const quadrantColors = {
    FLOW: 'rgba(76, 175, 80, 0.35)',      // Green
    ANXIÉTÉ: 'rgba(244, 67, 54, 0.35)',    // Red
    ENNUI: 'rgba(25, 118, 210, 0.35)',    // Blue
    INDIFFÉRENCE: 'rgba(158, 158, 158, 0.35)',   // Grey
    
    DETACHEMENT: 'rgba(13, 71, 161, 0.2)',  // Medium blue
    MAITRISE: 'rgba(76, 175, 80, 0.2)',       // Medium green
    EXCITATION: 'rgba(244, 67, 54, 0.2)',     // Medium red
    INQUIETUDE: 'rgba(255, 152, 0, 0.2)',     // Medium orange
  };

  // Add tooltip descriptions
  const tooltipDescriptions = {
    FLOW: "État optimal où les compétences correspondent parfaitement au niveau de défi. Favorise la concentration et la performance.",
    ANXIÉTÉ: "Le défi dépasse significativement les compétences actuelles, créant un sentiment de stress et d'anxiété.",
    ENNUI: "Les compétences dépassent largement le niveau de défi, menant à un désengagement par ennui.",
    INDIFFÉRENCE: "Faible niveau de défi et de compétences, résultant en un manque d'engagement et de motivation.",
    EXCITATION: "Zone de transition vers le flow, avec un défi légèrement supérieur aux compétences.",
    MAITRISE: "Zone de confort où les compétences permettent une bonne gestion des défis.",
    DETACHEMENT: "Niveau de défi insuffisant par rapport aux compétences, risque de désengagement.",
    INQUIETUDE: "Les défis commencent à dépasser les compétences, créant une légère tension.",
    NEUTRE: "Zone d'équilibre modéré entre les compétences et les défis."
  };

  return (
    <Paper sx={{ p: 3, height: '100%' }}>
      <Typography variant="h6" gutterBottom align="center">
        Guide des États de Flow
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 2 }}>
        Le graphique représente la relation entre le niveau de compétence (axe X) 
        et le niveau de défi (axe Y) pour chaque activité.
      </Typography>

      <Box sx={{ height: 200, position: 'relative' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart
            margin={{ top: 10, right: 10, bottom: 20, left: 20 }}
          >
            {/* Main quadrants */}
            <ReferenceArea x1={0.66} x2={1} y1={0.66} y2={1} fill={quadrantColors.FLOW} />
            <ReferenceArea x1={0} x2={0.33} y1={0.66} y2={1} fill={quadrantColors.ANXIÉTÉ} />
            <ReferenceArea x1={0.66} x2={1} y1={0} y2={0.33} fill={quadrantColors.ENNUI} />
            <ReferenceArea x1={0} x2={0.33} y1={0} y2={0.33} fill={quadrantColors.INDIFFÉRENCE} />
            
            {/* Buffer zones */}
            <ReferenceArea x1={0.33} x2={0.66} y1={0.66} y2={1} fill={quadrantColors.EXCITATION} />
            <ReferenceArea x1={0.66} x2={1} y1={0.33} y2={0.66} fill={quadrantColors.MAITRISE} />
            <ReferenceArea x1={0.33} x2={0.66} y1={0} y2={0.33} fill={quadrantColors.DETACHEMENT} />
            <ReferenceArea x1={0} x2={0.33} y1={0.33} y2={0.66} fill={quadrantColors.INQUIETUDE} />
            <ReferenceArea x1={0.33} x2={0.66} y1={0.33} y2={0.66} fill="rgba(189, 189, 189, 0.1)" />
            
            {/* Grid lines */}
            <line x1="33%" y1="0" x2="33%" y2="100%" stroke="#666" strokeDasharray="3 3" />
            <line x1="66%" y1="0" x2="66%" y2="100%" stroke="#666" strokeDasharray="3 3" />
            <line x1="0" y1="33%" x2="100%" y2="33%" stroke="#666" strokeDasharray="3 3" />
            <line x1="0" y1="66%" x2="100%" y2="66%" stroke="#666" strokeDasharray="3 3" />
          </ScatterChart>
        </ResponsiveContainer>

        {/* Update buffer zone labels with tooltips */}
        <Tooltip title={tooltipDescriptions.EXCITATION} arrow placement="top">
          <Typography 
            sx={{ 
              position: 'absolute',
              width: '34%',
              left: '33%',
              top: '8%',
              fontSize: '0.8rem',
              color: '#d32f2f',
              textAlign: 'center',
              cursor: 'help'  // Add cursor style to indicate hoverable
            }}
          >
            EXCITATION
          </Typography>
        </Tooltip>

        {/* Continue updating other labels similarly. Here's one more example: */}
        <Tooltip title={tooltipDescriptions.FLOW} arrow placement="top">
          <Typography 
            sx={{ 
              position: 'absolute',
              width: '34%',
              right: '0',
              top: '8%',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              color: '#2e7d32',
              textAlign: 'center',
              cursor: 'help'
            }}
          >
            FLOW
          </Typography>
        </Tooltip>

        {/* Top row */}
        <Tooltip title={tooltipDescriptions.ANXIÉTÉ} arrow placement="top">
          <Typography 
            sx={{ 
              position: 'absolute',
              width: '34%',
              left: '0',
              top: '8%',
              fontSize: '0.8rem',
              color: '#d32f2f',
              textAlign: 'center',
              cursor: 'help'
            }}
          >
            ANXIÉTÉ
          </Typography>
        </Tooltip>

        <Tooltip title={tooltipDescriptions.EXCITATION} arrow placement="top">
          <Typography 
            sx={{ 
              position: 'absolute',
              width: '34%',
              left: '33%',
              top: '8%',
              fontSize: '0.8rem',
              color: '#d32f2f',
              textAlign: 'center',
              cursor: 'help'
            }}
          >
            EXCITATION
          </Typography>
        </Tooltip>

        <Tooltip title={tooltipDescriptions.FLOW} arrow placement="top">
          <Typography 
            sx={{ 
              position: 'absolute',
              width: '34%',
              right: '0',
              top: '8%',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              color: '#2e7d32',
              textAlign: 'center',
              cursor: 'help'
            }}
          >
            FLOW
          </Typography>
        </Tooltip>

        {/* Middle row */}
        <Tooltip title={tooltipDescriptions.INQUIETUDE} arrow placement="left">
          <Typography 
            sx={{ 
              position: 'absolute',
              width: '34%',
              left: '0',
              top: '45%',
              fontSize: '0.8rem',
              color: '#ed6c02',
              textAlign: 'center',
              cursor: 'help'
            }}
          >
            INQUIETUDE
          </Typography>
        </Tooltip>

        <Tooltip title={tooltipDescriptions.MAITRISE} arrow placement="right">
          <Typography 
            sx={{ 
              position: 'absolute',
              width: '34%',
              right: '0',
              top: '45%',
              fontSize: '0.8rem',
              color: '#2e7d32',
              textAlign: 'center',
              cursor: 'help'
            }}
          >
            MAITRISE
          </Typography>
        </Tooltip>

        {/* Bottom row */}
        <Tooltip title={tooltipDescriptions.INDIFFÉRENCE} arrow placement="bottom">
          <Typography 
            sx={{ 
              position: 'absolute',
              width: '34%',
              left: '0',
              bottom: '8%',
              fontSize: '0.8rem',
              color: '#757575',
              textAlign: 'center',
              cursor: 'help'
            }}
          >
            INDIFFÉRENCE
          </Typography>
        </Tooltip>

        <Tooltip title={tooltipDescriptions.DETACHEMENT} arrow placement="bottom">
          <Typography 
            sx={{ 
              position: 'absolute',
              width: '34%',
              left: '33%',
              bottom: '8%',
              fontSize: '0.8rem',
              color: '#0d47a1',
              textAlign: 'center',
              cursor: 'help'
            }}
          >
            DETACHEMENT
          </Typography>
        </Tooltip>

        <Tooltip title={tooltipDescriptions.ENNUI} arrow placement="bottom">
          <Typography 
            sx={{ 
              position: 'absolute',
              width: '34%',
              right: '0',
              bottom: '8%',
              fontSize: '0.8rem',
              color: '#1976d2',
              textAlign: 'center',
              cursor: 'help'
            }}
          >
            ENNUI
          </Typography>
        </Tooltip>

        <Tooltip title={tooltipDescriptions.NEUTRE} arrow placement="right">
          <Typography 
            sx={{ 
              position: 'absolute',
              width: '34%',
              left: '33%',
              top: '45%',
              fontSize: '0.8rem',
              color: '#757575',
              textAlign: 'center',
              cursor: 'help'
            }}
          >
            NEUTRE
          </Typography>
        </Tooltip>
      </Box>

      {/* Axis Labels */}
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Niveau de Compétence →
        </Typography>
      </Box>
      <Box 
        sx={{ 
          position: 'absolute', 
          left: 10, 
          top: '50%', 
          transform: 'rotate(-90deg) translateX(-50%)',
          transformOrigin: 'left top'
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Niveau de Défi →
        </Typography>
      </Box>
    </Paper>
  );
};

export default FlowStateLegend;